'use client'

import { useAuth } from '@clerk/nextjs'
import type { ApiErrorBody } from '@nivex/contracts'
import { useCallback, useRef, useState } from 'react'
import { env } from '@/config/env'
import { AppError } from '@/lib/errors/appError'

interface UploadOptions {
  method?: 'POST' | 'PUT' | 'PATCH'
  headers?: Record<string, string>
}

/**
 * `useApiFetch`/`apiFetch` are `fetch`-based and have no upload-progress event — this is the
 * dedicated `XMLHttpRequest`-based seam for CAS PDF/tradebook/document uploads that need real
 * byte-level progress and cancellation (`frontend-code-standards.md` §13). Decided in Phase 0
 * since it shares the fetch layer's concerns, first exercised in Phase 7.
 */
export function useFileUpload() {
  const { getToken } = useAuth()
  const [progress, setProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const xhrRef = useRef<XMLHttpRequest | null>(null)

  const upload = useCallback(
    async <T>(path: string, formData: FormData, options: UploadOptions = {}): Promise<T> => {
      const token = await getToken()
      setIsUploading(true)
      setProgress(0)

      try {
        return await new Promise<T>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhrRef.current = xhr

          xhr.open(options.method ?? 'POST', `${env.NEXT_PUBLIC_API_URL}${path}`)
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
          for (const [key, value] of Object.entries(options.headers ?? {})) {
            xhr.setRequestHeader(key, value)
          }

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) setProgress(Math.round((event.loaded / event.total) * 100))
          }

          xhr.onload = () => {
            const body: unknown = xhr.responseText ? JSON.parse(xhr.responseText) : undefined
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(body as T)
            } else {
              reject(AppError.fromResponse(xhr.status, body as Partial<ApiErrorBody> | null))
            }
          }
          xhr.onerror = () => reject(AppError.fromUnknown(new TypeError('Failed to fetch')))
          xhr.onabort = () => reject(new AppError('timeout', 'Upload cancelled.'))

          xhr.send(formData)
        })
      } finally {
        setIsUploading(false)
        xhrRef.current = null
      }
    },
    [getToken],
  )

  const cancel = useCallback(() => xhrRef.current?.abort(), [])

  return { upload, progress, isUploading, cancel }
}
